<template>
  <dx-modal :value="show" title="Consolidar repasses" size="lg"
            :closable="!saving" @input="close()" id="modal-transfer-consolidate">
    <div class="stenci-group stenci-alert tr-help">
      <p>
        A consolidação cria uma conta a pagar pendente para cada profissional com os itens
        selecionados. Ajustes de glosa pendentes do profissional entram no mesmo título.
        O pagamento é feito depois, na tela de títulos.
      </p>
    </div>

    <p class="tr-section-title">Resumo por profissional</p>
    <div class="loading loading-lg tr-cons-loading" v-if="loading"></div>
    <template v-else>
      <div class="tr-cons-group" v-for="item in preview" :key="String(item.professionalId)"
           :class="{excluded: !included[String(item.professionalId)], skipped: item.skipped}">
        <div class="tr-cons-head">
          <label class="form-checkbox" v-if="!item.skipped">
            <input type="checkbox" :checked="included[String(item.professionalId)]"
                   @change="toggleIncluded(item, $event.target.checked)">
            <i class="form-icon"></i>
          </label>
          <div class="tr-avatar">{{ initials(item.name) }}</div>
          <div>
            <div class="tr-cons-name">{{ item.name }}</div>
            <div class="tr-cons-council">{{ councilOf(item) }}</div>
          </div>
          <div class="tr-cons-total" :class="{negative: item.total <= 0}">
            <small>{{ item.total > 0 ? 'Conta a pagar' : 'Sem conta a pagar' }}</small>
            <b class="tr-num">{{ item.total | currency }}</b>
          </div>
        </div>
        <div class="tr-cons-line" v-if="item.counts.cashRegister > 0">
          <fa-icon class="tr-cons-icon" :icon="['fal', 'cash-register']"/>
          <span class="tr-cons-label">
            <b>Particular (caixa)</b>
            <span> · {{ countLabel(item.counts.cashRegister, 'item', 'itens') }}</span>
          </span>
          <span class="tr-num">{{ sumOf(item.cashRegister) | currency }}</span>
        </div>
        <div class="tr-cons-line" v-if="item.counts.medicalReport > 0">
          <fa-icon class="tr-cons-icon" :icon="['fal', 'file-signature']"/>
          <span class="tr-cons-label">
            <b>Laudos</b>
            <span> · {{ countLabel(item.counts.medicalReport, 'laudo', 'laudos') }}</span>
          </span>
          <span class="tr-num">{{ sumOf(item.medicalReports) | currency }}</span>
        </div>
        <div class="tr-cons-line adjust" v-if="item.adjustments.length > 0">
          <fa-icon class="tr-cons-icon" :icon="['fal', 'clock']"/>
          <span class="tr-cons-label">
            <b>Ajustes pendentes absorvidos</b>
            <span> · {{ adjustmentsLabel(item.adjustments) }}</span>
          </span>
          <span class="tr-num" :class="item.adjustmentsNet < 0 ? 'text-error' : 'text-green'">
            {{ signedCurrency(item.adjustmentsNet) }}
          </span>
        </div>
        <div class="tr-cons-line warning" v-if="item.skipped">
          <fa-icon class="tr-cons-icon" :icon="['fal', 'exclamation-triangle']"/>
          <span class="tr-cons-label">{{ skipReason(item) }}</span>
          <span></span>
        </div>
      </div>
    </template>

    <p class="tr-section-title mt-2">Dados das contas a pagar</p>
    <div class="columns">
      <div class="column col-3 col-sm-12 form-group" :class="{'has-error': $v.form.dueDate.$error}">
        <label class="form-label">Vencimento</label>
        <dx-input-date id="transfer-due-date" class="form-input" v-model="form.dueDate"
                       placeholder="00/00/0000" @blur="$v.form.dueDate.$touch()"/>
        <template v-if="$v.form.dueDate.$error">
          <div class="form-input-hint" v-if="!$v.form.dueDate.required">Campo obrigatório</div>
          <div class="form-input-hint" v-else-if="!$v.form.dueDate.date">Data inválida</div>
          <div class="form-input-hint" v-else-if="!$v.form.dueDate.minDate">
            Não pode ser anterior a hoje
          </div>
        </template>
      </div>
      <div class="column col-5 col-sm-12 form-group"
           :class="{'has-error': $v.form.categoryId.$error}">
        <label class="form-label">Categoria</label>
        <dx-autocomplete id="transfer-category" v-model="category" :readonly="!!form.categoryId"
                         :source="findCategory" label="name" track-by="id" :debounce="800"
                         @select="setCategory" @blur="$v.form.categoryId.$touch()"
                         placeholder="Informe o nome da categoria...">
          <button slot="action" v-if="category" tabindex="-1"
                  class="btn btn-action input-group-btn btn-icon btn-gray"
                  @click="setCategory(null)">
            <fa-icon :icon="['fal', 'times']"/>
          </button>
          <button slot="action" v-else class="btn btn-action input-group-btn btn-icon btn-neutral"
                  tabindex="-1">
            <fa-icon :icon="['fal', 'search']"/>
          </button>
          <template v-slot="{ item }">
            <div v-if="item.id === null">Categoria não encontrada!</div>
            <div v-else>
              <span v-if="item.parent">{{ item.parent.name }} > </span>
              <strong>{{ item.name }}</strong>
            </div>
          </template>
        </dx-autocomplete>
        <div class="form-input-hint" v-if="$v.form.categoryId.$error">Campo obrigatório</div>
      </div>
      <div class="column col-4 col-sm-12 form-group">
        <label class="form-label">Centro de custo <span class="text-gray">(opcional)</span></label>
        <dx-autocomplete id="transfer-cost-center" v-model="costCenter"
                         :readonly="!!form.costCenterId"
                         :source="findCostCenter" label="name" track-by="id" :debounce="800"
                         @select="setCostCenter" placeholder="Informe o nome...">
          <button slot="action" v-if="costCenter" tabindex="-1"
                  class="btn btn-action input-group-btn btn-icon btn-gray"
                  @click="setCostCenter(null)">
            <fa-icon :icon="['fal', 'times']"/>
          </button>
          <button slot="action" v-else class="btn btn-action input-group-btn btn-icon btn-neutral"
                  tabindex="-1">
            <fa-icon :icon="['fal', 'search']"/>
          </button>
          <template v-slot="{ item }">
            <div v-if="item.id === null">Centro de custo não encontrado!</div>
            <div v-else>
              <span v-if="item.parent">{{ item.parent.name }} > </span>
              <strong>{{ item.name }}</strong>
            </div>
          </template>
        </dx-autocomplete>
      </div>
      <div class="column col-12 form-group">
        <label class="form-label">
          Observação <span class="text-gray">(vai para o título)</span>
        </label>
        <textarea class="form-input" rows="2" v-model="form.notes" maxlength="500"
                  placeholder="Ex.: repasse de agosto — particular e laudos"></textarea>
      </div>
    </div>

    <div class="tr-cons-total">
      <span class="tr-cons-total-label">
        {{ includedCount }} {{ includedCount === 1 ? 'conta a pagar' : 'contas a pagar' }}
      </span>
      <b class="tr-num">{{ includedTotal | currency }}</b>
    </div>

    <template slot="footer">
      <button class="btn btn-primary btn-icon btn-icon-left mr-1"
              :class="{loading: saving}" :disabled="saving || loading || includedCount === 0"
              @click="save">
        <fa-icon :icon="['fal', 'check']"/>Consolidar
      </button>
      <button class="btn" :disabled="saving" @click="close()">Sair</button>
    </template>
  </dx-modal>
</template>

<script>
import moment from 'moment';
import { required } from 'vuelidate/lib/validators';
import { date, minDate } from '@/data/validators';
import {
  initials, councilLabel, signedCurrency, currency,
} from '../helpers';

export default {
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    groups: {
      type: Array,
      default: () => [],
    },
    defaults: {
      type: Object,
      default: () => ({ paymentDays: 30, categoryId: null, costCenterId: null }),
    },
  },
  data() {
    return {
      loading: true,
      saving: false,
      preview: [],
      included: {},
      category: null,
      costCenter: null,
      form: {
        dueDate: '',
        categoryId: '',
        costCenterId: '',
        notes: '',
      },
    };
  },
  validations() {
    return {
      form: {
        dueDate: { required, date, minDate: minDate(moment().format('YYYY-MM-DD')) },
        categoryId: { required },
      },
    };
  },
  computed: {
    includedGroups() {
      return this.preview
        .filter(item => !item.skipped && this.included[String(item.professionalId)]);
    },
    includedCount() {
      return this.includedGroups.length;
    },
    includedTotal() {
      return this.includedGroups.reduce((sum, item) => sum + (Number(item.total) || 0), 0);
    },
  },
  mounted() {
    this.init();
  },
  methods: {
    initials,
    signedCurrency,
    countLabel(count, singular, plural) {
      return `${count} ${count === 1 ? singular : plural}`;
    },
    async init() {
      const days = Number(this.defaults.paymentDays) || 30;
      this.form.dueDate = moment().add(days, 'days').format('YYYY-MM-DD');
      this.form.notes = '';
      await Promise.all([
        this.loadPreview(),
        this.defaults.categoryId ? this.getCategory(this.defaults.categoryId) : null,
        this.defaults.costCenterId ? this.getCostCenter(this.defaults.costCenterId) : null,
      ]);
    },
    payload(groups) {
      return {
        professionals: groups.map(group => ({
          professionalId: String(group.professionalId),
          cashRegisterItemIds: group.cashRegisterItemIds.map(item => ({
            entryId: String(item.entryId),
            itemId: String(item.itemId),
          })),
          medicalReportIds: group.medicalReportIds.map(String),
        })),
      };
    },
    loadPreview() {
      this.loading = true;
      return this.$http.post('/billing-transfers/consolidate/preview', this.payload(this.groups))
        .then(({ data }) => {
          this.preview = data.professionals || [];
          const included = {};
          this.preview.forEach((item) => {
            included[String(item.professionalId)] = !item.skipped;
          });
          this.included = included;
        })
        .catch((e) => {
          this.$toast.error(e);
          this.close();
        })
        .finally(() => {
          this.loading = false;
        });
    },
    councilOf(item) {
      const group = this.groups.find(({ professionalId }) => (
        String(professionalId) === String(item.professionalId)
      ));
      return group ? councilLabel(group.professional) : '';
    },
    sumOf(rows) {
      return (rows || []).reduce((sum, row) => sum + (Number(row.transfer) || 0), 0);
    },
    adjustmentsLabel(adjustments) {
      const debits = adjustments.filter(({ kind }) => kind === 'debit');
      const credits = adjustments.filter(({ kind }) => kind === 'credit');
      const parts = [];
      if (debits.length > 0) {
        const total = debits.reduce((sum, item) => sum + (Number(item.value) || 0), 0);
        parts.push(`${this.countLabel(debits.length, 'débito', 'débitos')} ${signedCurrency(-total)}`);
      }
      if (credits.length > 0) {
        const total = credits.reduce((sum, item) => sum + (Number(item.value) || 0), 0);
        parts.push(`${this.countLabel(credits.length, 'crédito', 'créditos')} ${signedCurrency(total)}`);
      }
      return parts.join(' · ');
    },
    skipReason(item) {
      if (item.reason === 'noItems') return 'Nenhum item pendente para este profissional.';
      return `Saldo de ${currency(item.total)} não gera conta a pagar; `
        + 'os ajustes continuam pendentes.';
    },
    toggleIncluded(item, checked) {
      this.included = { ...this.included, [String(item.professionalId)]: checked };
    },
    findCategory(search) {
      const params = {
        active: true,
        allowsValue: true,
        type: 'expense',
        limit: 0,
        search,
      };
      return this.$http.get('/categories', { params })
        .then(({ data }) => (data.items.length === 0 ? [{ id: null, name: search }] : data.items))
        .catch(() => []);
    },
    setCategory(data) {
      this.category = null;
      this.form.categoryId = '';
      if (data && data.id) {
        this.category = { id: data.id, name: data.name };
        this.form.categoryId = data.id;
      }
    },
    getCategory(id) {
      return this.$http.get(`/categories/${id}`)
        .then(({ data }) => {
          if (data && data.id) this.setCategory(data);
        })
        .catch(() => {});
    },
    findCostCenter(search) {
      const params = {
        active: true,
        allowsValue: true,
        limit: 0,
        search,
      };
      return this.$http.get('/cost-centers', { params })
        .then(({ data }) => (data.items.length === 0 ? [{ id: null, name: search }] : data.items))
        .catch(() => []);
    },
    setCostCenter(data) {
      this.costCenter = null;
      this.form.costCenterId = '';
      if (data && data.id) {
        this.costCenter = { id: data.id, name: data.name };
        this.form.costCenterId = data.id;
      }
    },
    getCostCenter(id) {
      return this.$http.get(`/cost-centers/${id}`)
        .then(({ data }) => {
          if (data && data.id) this.setCostCenter(data);
        })
        .catch(() => {});
    },
    save() {
      this.$v.form.$touch();
      if (this.$v.form.$error || this.includedCount === 0) return;

      const ids = new Set(this.includedGroups.map(item => String(item.professionalId)));
      const groups = this.groups.filter(group => ids.has(String(group.professionalId)));

      this.saving = true;
      this.$http.post('/billing-transfers/consolidate', {
        ...this.payload(groups),
        dueDate: this.form.dueDate,
        categoryId: this.form.categoryId || null,
        costCenterId: this.form.costCenterId || null,
        notes: this.form.notes || null,
      })
        .then(({ data }) => {
          const count = data.totals ? data.totals.count : (data.created || []).length;
          const label = this.countLabel(count, 'conta a pagar criada', 'contas a pagar criadas');
          this.$toast.success(`Repasses consolidados: ${label}.`);
          this.$emit('saved', data);
          this.close(true);
        })
        .catch((e) => {
          this.$toast.error(e);
        })
        .finally(() => {
          this.saving = false;
        });
    },
    close(saved = false) {
      this.$emit('close', saved);
    },
  },
};
</script>
